import { useState } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function MatchIcon({ match }) {
  if (match === true) {
    return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  }
  if (match === false) {
    return <AlertTriangle className="h-4 w-4 text-red-500" />;
  }
  return <span className="text-muted-foreground">?</span>;
}

export function InvoiceStatusCheckPanel() {
  const [clientName, setClientName] = useState('PHD Access');
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    const name = clientName.trim();
    if (!name) {
      toast.error('Enter a client name');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosInvoiceStatusCheck({
        client_name: name,
        limit,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') runCheck();
  };

  const rows = result?.rows;
  const hasRows = rows && rows.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS Invoice Status Check</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Compare overdue invoices in our DB against the live BR OS API.
          Detects stale statuses from sync lag.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Client name"
            className="border rounded px-3 py-1.5 text-sm w-48 bg-background"
          />
          <input
            type="number"
            value={limit}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setLimit(Math.max(1, Math.min(50, v)));
            }}
            min={1}
            max={50}
            className="border rounded px-3 py-1.5 text-sm w-20 bg-background"
          />
          <Button onClick={runCheck} disabled={loading || !clientName.trim()}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>

        {result?.api_error && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" />
            API error: {result.api_error}
          </div>
        )}

        {result && !result.api_error && (
          <div className="space-y-4">
            {/* Summary banner */}
            <div
              className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${
                result.mismatches > 0
                  ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 text-amber-700 dark:text-amber-300'
                  : 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950 text-green-700 dark:text-green-300'
              }`}
            >
              {result.mismatches > 0 ? (
                <AlertTriangle className="h-4 w-4 shrink-0" />
              ) : (
                <CheckCircle2 className="h-4 w-4 shrink-0" />
              )}
              {result.mismatches} mismatch{result.mismatches !== 1 ? 'es' : ''} out of{' '}
              {result.row_count} invoice{result.row_count !== 1 ? 's' : ''} checked
              {result.not_found_in_api > 0 && (
                <span className="ml-2 text-xs text-muted-foreground">
                  ({result.not_found_in_api} not found in API)
                </span>
              )}
            </div>

            {/* Results table */}
            {hasRows && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Invoice ID</th>
                      <th className="text-left px-2 py-1.5 font-medium">DB Status</th>
                      <th className="text-left px-2 py-1.5 font-medium">API Status</th>
                      <th className="text-left px-2 py-1.5 font-medium">API Field Name</th>
                      <th className="text-center px-2 py-1.5 font-medium">Match?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.bros_invoice_id} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono align-top">
                          <details>
                            <summary className="cursor-pointer">
                              {row.bros_invoice_id}
                            </summary>
                            <div className="mt-2 space-y-2">
                              <div>
                                <p className="font-medium text-muted-foreground mb-1">our_db</p>
                                <pre className="border rounded p-2 overflow-x-auto bg-muted/30 text-[10px] max-h-48 overflow-y-auto whitespace-pre-wrap">
                                  {JSON.stringify(row.our_db, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <p className="font-medium text-muted-foreground mb-1">br_os_api</p>
                                <pre className="border rounded p-2 overflow-x-auto bg-muted/30 text-[10px] max-h-48 overflow-y-auto whitespace-pre-wrap">
                                  {JSON.stringify(row.br_os_api, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </details>
                        </td>
                        <td className="px-2 py-1 align-top">
                          <Badge variant="outline">{row.our_db?.status ?? '-'}</Badge>
                        </td>
                        <td className="px-2 py-1 align-top">
                          {row.br_os_api?.found_in_api === false ? (
                            <span className="text-muted-foreground italic">not found</span>
                          ) : (
                            <Badge
                              variant={
                                String(row.br_os_api?.status_field_value).toLowerCase() === 'paid'
                                  ? 'default'
                                  : 'outline'
                              }
                            >
                              {row.br_os_api?.status_field_value ?? '-'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-2 py-1 font-mono align-top">
                          {row.br_os_api?.status_field_name ?? '-'}
                        </td>
                        <td className="px-2 py-1 text-center align-top">
                          <MatchIcon match={row.match} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Empty rows message */}
            {!hasRows && result.message && (
              <p className="text-sm text-muted-foreground">{result.message}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
